/**
 * Browser error reporting (ADR 0041).
 *
 * Before this, apps/web sent nothing anywhere — three bare `console.error`
 * calls and no telemetry — so "the page went blank" produced zero
 * server-side evidence and no way to correlate to a backend request.
 *
 * Deliberately NOT the Application Insights JS SDK: that needs a
 * browser-side connection string and a publicly-reachable ingestion
 * endpoint, which fights the everything-inside-the-tenant posture. One
 * authenticated POST back through the Front Door path we already use
 * costs nothing extra and keeps the data in the same log stream as the
 * backend's, joinable on `request_id`.
 */
import type { App } from "vue";
import { acquireToken, USE_REAL_AUTH } from "./auth/msal";

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

/**
 * Client-side throttle. The server enforces its own limit too — this one
 * exists so a component stuck in a render loop doesn't spend the user's
 * bandwidth discovering that.
 */
const MAX_REPORTS_PER_SESSION = 25;
let sent = 0;

/** Dedupe identical messages; a loop otherwise reports the same line forever. */
const seen = new Set<string>();

/**
 * The last `X-Request-Id` the API returned. Attaching it is what lets an
 * operator pivot from a browser error to the backend request that caused
 * it — see the Logs tab's "jump to request id".
 */
let lastRequestId = "";

export function noteRequestId(id: string | null | undefined): void {
  if (id) lastRequestId = id;
}

export async function reportClientError(
  msg: string,
  stack = "",
  route = "",
): Promise<void> {
  if (sent >= MAX_REPORTS_PER_SESSION) return;
  const key = `${msg}::${route}`;
  if (seen.has(key)) return;
  seen.add(key);
  sent += 1;

  try {
    // The endpoint records the error against the reporting user, so it
    // needs a real credential — without one it 401s and every report is
    // silently dropped, which is how you ship error reporting that
    // reports nothing. Imported from auth/msal rather than api/admin to
    // avoid a cycle: admin.ts imports noteRequestId from here.
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (USE_REAL_AUTH) {
      const t = await acquireToken();
      if (!t) return; // not signed in yet — nothing useful to attribute
      headers.Authorization = `Bearer ${t.token}`;
    }

    await fetch(`${API_BASE}/api/client-errors`, {
      method: "POST",
      headers,
      // Errors are diagnostics, not user actions: never let a failed
      // report block or retry into the user's way.
      keepalive: true,
      body: JSON.stringify({
        msg: String(msg).slice(0, 2000),
        stack: String(stack).slice(0, 8000),
        route: route || window.location.hash || window.location.pathname,
        request_id: lastRequestId,
      }),
    });
  } catch {
    // Reporting an error must never itself throw — that would turn one
    // broken component into an unhandled-rejection loop.
  }
}

/** Install global handlers. Call once, before `app.mount`. */
export function installErrorReporting(app: App): void {
  app.config.errorHandler = (err, _instance, info) => {
    const e = err as Error;
    // Keep the console behaviour developers rely on locally.
    console.error("[vue]", err, info);
    void reportClientError(e?.message ?? String(err), e?.stack ?? "", info);
  };

  window.addEventListener("error", (ev) => {
    void reportClientError(
      ev.message || "window.onerror",
      ev.error?.stack ?? `${ev.filename}:${ev.lineno}:${ev.colno}`,
    );
  });

  window.addEventListener("unhandledrejection", (ev) => {
    const reason = ev.reason;
    void reportClientError(
      reason?.message ?? String(reason),
      reason?.stack ?? "",
      "unhandledrejection",
    );
  });
}
